import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronsLeftRight, Sparkles, Clock3, Gauge } from "lucide-react";
import { SMILE_GALLERY } from "../data/content";
import { Kicker } from "./fx";
import { scrollToId } from "../lib/scroll";

const EASE = [0.22, 1, 0.36, 1];
const BEFORE_FILTER = "grayscale(0.5) sepia(0.35) brightness(0.78) contrast(0.95)";
const AFTER_FILTER = "brightness(1.08) saturate(1.18) contrast(1.05)";

export default function SmileGallery() {
  const [caseIdx, setCaseIdx] = useState(0);
  const [pos, setPos] = useState(50);
  const trackRef = useRef(null);
  const dragging = useRef(false);
  const active = SMILE_GALLERY[caseIdx];

  const move = (clientX) => {
    const r = trackRef.current?.getBoundingClientRect();
    if (!r) return;
    setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
  };

  const selectCase = (i) => {
    setCaseIdx(i);
    setPos(50);
  };

  return (
    <section
      id="gallery"
      data-testid="smile-gallery-section"
      className="relative overflow-hidden border-t border-white/10 bg-[#070B12] py-24 sm:py-32"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -left-32 top-10 h-[400px] w-[400px] rounded-full bg-amber-500/[0.06] blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 bottom-10 h-[360px] w-[360px] rounded-full bg-toothcyan/[0.06] blur-3xl"
      />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-10 flex flex-col justify-between gap-6 lg:flex-row lg:items-end"
        >
          <div className="max-w-2xl">
            <Kicker>Smile Transformations</Kicker>
            <h2 className="font-serif text-3xl font-light tracking-tight text-white sm:text-5xl">
              Drag the line —{" "}
              <em className="italic text-ember">witness the shift.</em>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-mist lg:pb-2 lg:text-right">
            Real clinical disciplines, representative results. Slide each case
            from before to after.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
          className="mb-8 flex flex-wrap gap-2.5"
        >
          {SMILE_GALLERY.map((c, i) => (
            <button
              key={c.id}
              data-testid={`gallery-case-tab-${c.id}`}
              onClick={() => selectCase(i)}
              className={`rounded-full border px-5 py-2.5 text-xs font-bold tracking-wide transition-all duration-300 active:scale-[0.97] ${
                i === caseIdx
                  ? "border-ember bg-ember text-[#070B12] shadow-[0_0_20px_rgba(232,178,92,0.35)]"
                  : "border-white/15 bg-white/[0.04] text-slate-300 hover:border-toothcyan/50 hover:text-white"
              }`}
            >
              {c.short}
            </button>
          ))}
        </motion.div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <motion.div
            initial={{
              opacity: 0,
              clipPath: "polygon(0 8%, 100% 0%, 100% 92%, 0 100%)",
            }}
            whileInView={{ opacity: 1, clipPath: "polygon(0 0%, 100% 0%, 100% 100%, 0 100%)" }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, ease: EASE }}
            className="lg:col-span-8"
          >
            <div
              ref={trackRef}
              data-testid="gallery-slider"
              onPointerDown={(e) => {
                dragging.current = true;
                e.currentTarget.setPointerCapture(e.pointerId);
                move(e.clientX);
              }}
              onPointerMove={(e) => dragging.current && move(e.clientX)}
              onPointerUp={() => (dragging.current = false)}
              onPointerCancel={() => (dragging.current = false)}
              className="relative aspect-[16/10] cursor-ew-resize touch-none select-none overflow-hidden rounded-[2rem] border border-white/10 bg-navy-deep shadow-[0_25px_70px_rgba(0,0,0,0.55)]"
            >
              <AnimatePresence mode="popLayout">
                <motion.img
                  key={`after-${active.id}`}
                  src={active.image}
                  alt={`${active.title} — after treatment`}
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease: EASE }}
                  className="absolute inset-0 h-full w-full object-cover"
                  style={{ filter: AFTER_FILTER }}
                  draggable={false}
                />
                <motion.img
                  key={`before-${active.id}`}
                  src={active.image}
                  alt={`${active.title} — before treatment`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease: EASE }}
                  className="absolute inset-0 h-full w-full object-cover"
                  style={{
                    filter: BEFORE_FILTER,
                    clipPath: `inset(0 ${100 - pos}% 0 0)`,
                  }}
                  draggable={false}
                />
              </AnimatePresence>

              <span className="absolute left-4 top-4 rounded-full border border-white/20 bg-[#070B12]/70 px-3.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-slate-300 backdrop-blur">
                Before
              </span>
              <span className="absolute right-4 top-4 flex items-center gap-1.5 rounded-full border border-ember/50 bg-[#070B12]/70 px-3.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-ember backdrop-blur">
                <Sparkles className="h-3 w-3" />
                After
              </span>

              <div
                aria-hidden
                className="absolute inset-y-0 w-[2px] bg-gradient-to-b from-toothcyan via-white to-ember"
                style={{ left: `calc(${pos}% - 1px)` }}
              />
              <button
                data-testid="gallery-slider-handle"
                role="slider"
                aria-label="Before and after comparison slider"
                aria-valuenow={Math.round(pos)}
                aria-valuemin={0}
                aria-valuemax={100}
                onKeyDown={(e) => {
                  if (e.key === "ArrowLeft")
                    setPos((p) => Math.max(4, p - 5));
                  if (e.key === "ArrowRight")
                    setPos((p) => Math.min(96, p + 5));
                }}
                className="absolute top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-[#070B12]/85 text-white shadow-[0_0_24px_rgba(23,210,245,0.35)] backdrop-blur transition-shadow focus:outline-none focus:ring-2 focus:ring-toothcyan"
                style={{ left: `${pos}%` }}
              >
                <ChevronsLeftRight className="h-5 w-5" />
              </button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
            className="lg:col-span-4"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.45, ease: EASE }}
                className="flex h-full flex-col rounded-[2rem] border border-white/10 bg-[#0F192C] p-7"
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-toothcyan">
                  Case {String(caseIdx + 1).padStart(2, "0")} / 03
                </span>
                <h3
                  data-testid="gallery-case-title"
                  className="mt-3 font-serif text-2xl font-semibold tracking-tight text-white"
                >
                  {active.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">
                  {active.desc}
                </p>
                <div className="mt-6 space-y-3 border-t border-white/10 pt-5">
                  <div className="flex items-center gap-2.5 text-sm text-slate-300">
                    <Clock3 className="h-4 w-4 text-ember" />
                    {active.timeline}
                  </div>
                  <div className="flex items-center gap-2.5 text-sm text-slate-300">
                    <Gauge className="h-4 w-4 text-ember" />
                    {active.metric}
                  </div>
                </div>
                <button
                  data-testid="gallery-book-case-button"
                  onClick={() => scrollToId("#book")}
                  className="mt-auto inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-ember to-amber-500 px-6 py-3 text-xs font-bold text-[#070B12] transition-shadow duration-300 hover:shadow-[0_0_28px_rgba(232,178,92,0.4)] active:scale-[0.97]"
                >
                  Start my transformation
                </button>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>

        <p className="mt-6 text-center font-mono text-[9px] uppercase tracking-[0.18em] text-mist/60">
          Representative imagery · the clinic's real patient case archive is
          updated regularly
        </p>
      </div>
    </section>
  );
}
